/**
 * Sidebar top brand row (logo + title) with a hidden multi-tap easter egg.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { APP_DISPLAY_TIMEZONE } from '@/lib/datetime-timezone';
import logo from '@/assets/logo/trafficpulse.png';

import { useSidebarLogoEasterEgg } from './useSidebarLogoEasterEgg';

const danishTimeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: APP_DISPLAY_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Live Danish (Copenhagen) time — so the hour is unambiguous wherever you are. */
function DanishClock({ collapsed = false }: { collapsed?: boolean }) {
  const [time, setTime] = useState(() => danishTimeFmt.format(new Date()));
  useEffect(() => {
    const id = window.setInterval(() => setTime(danishTimeFmt.format(new Date())), 15_000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div
      className={cn(
        'flex items-center gap-1 font-medium tabular-nums text-muted-foreground',
        collapsed ? 'text-[9px] leading-none' : 'text-[11px]',
      )}
      title="Danish time — Copenhagen (Europe/Copenhagen)"
    >
      <span
        className={cn('shrink-0 leading-none', collapsed ? 'text-[10px]' : 'text-sm')}
        aria-hidden
      >
        🇩🇰
      </span>
      <span>{time}</span>
    </div>
  );
}

function getEnvironmentBadge() {
  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  const isDevEnvironment =
    import.meta.env.DEV ||
    apiUrl.includes('localhost') ||
    apiUrl.includes('-dev.') ||
    hostname === 'localhost' ||
    hostname.includes('-dev.');

  return {
    label: isDevEnvironment ? 'DEV' : 'BETA',
    title: isDevEnvironment ? 'Development environment' : 'This product is in beta',
  };
}

export function SidebarBrandBar({ collapsed }: { collapsed: boolean }) {
  const { onBrandBarPointerDown } = useSidebarLogoEasterEgg();
  const badge = getEnvironmentBadge();

  return (
    <div
      role="presentation"
      onPointerDown={onBrandBarPointerDown}
      className={cn(
        'flex h-16 select-none items-center border-b',
        collapsed ? 'justify-center px-3' : 'px-6',
      )}
    >
      {!collapsed && (
        <div className="mr-3 flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            <span className="text-xl font-bold text-primary">TrafficPulse</span>
            <span
              className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
              title={badge.title}
            >
              {badge.label}
            </span>
          </div>
          <DanishClock />
        </div>
      )}
      <div className={cn('flex flex-col items-center', collapsed && 'gap-0.5')}>
        <img src={logo} alt="TrafficPulse" className={cn('w-auto', collapsed ? 'h-8' : 'h-8')} />
        {collapsed && (
          <>
            <span
              className="text-[8px] font-semibold uppercase leading-none tracking-wider text-muted-foreground"
              title={badge.title}
            >
              {badge.label}
            </span>
            <DanishClock collapsed />
          </>
        )}
      </div>
    </div>
  );
}
